<template>
  <header class="nav no-print">
    <!-- Brand -->
    <div
      class="nav-brand"
      style="cursor: pointer"
      @click="handleReset"
    >
      <div class="logo-wrap">
        <img :src="cptWordmark" :alt="appConfig.brandName" class="logo-image" />
      </div>
    </div>

    <!-- Nav Links (desktop) -->
    <nav class="hide-mobile-flex gap-xl">
      <button v-if="hasActiveSession" class="nav-link nav-link-danger" @click="handleDeleteData">{{ t('nav.deleteData') }}</button>
      <button class="nav-link" @click="handleSupport">{{ t('nav.support') }}</button>
      <router-link :to="localePath('/contact')" class="nav-link">{{ t('nav.contact') }}</router-link>
      <router-link :to="localePath('/blog')" class="nav-link">{{ t('nav.blog') }}</router-link>
      <button v-if="hasActiveReport" class="nav-link nav-link-report" @click="handleBackToReport">{{ t('nav.backToReport') }}</button>
    </nav>

    <!-- Right Side: Get Report CTA + Language + Hamburger -->
    <div class="flex-center gap-md">
      <button class="btn-cta-nav hide-mobile" @click="navigateToOAuth">{{ t('hero.ctaPrimary') }}</button>
      <!-- Language Selector — a searchable combobox (one control, desktop +
           mobile). Click to open a search input + filterable list; type to
           filter by endonym, English name, or ISO code. Data-driven from
           @/i18n supportedLocales, so new locales appear automatically. -->
      <LanguageCombobox />

      <!-- Hamburger (mobile only) -->
      <button class="hamburger-btn" @click="toggleMenu" aria-label="Menu">
        <svg v-if="!menuOpen" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>

    <!-- Mobile dropdown menu (nav links only — language lives in the always-visible combobox above) -->
    <nav v-if="menuOpen" class="mobile-nav-dropdown">
      <button v-if="hasActiveSession" class="mobile-nav-link mobile-nav-link-danger" @click="handleDeleteDataMobile">{{ t('nav.deleteData') }}</button>
      <button class="mobile-nav-link" @click="handleSupportMobile">{{ t('nav.support') }}</button>
      <router-link :to="localePath('/contact')" class="mobile-nav-link" @click="menuOpen = false">{{ t('nav.contact') }}</router-link>
      <router-link :to="localePath('/blog')" class="mobile-nav-link" @click="menuOpen = false">{{ t('nav.blog') }}</router-link>
      <button v-if="hasActiveReport" class="mobile-nav-link mobile-nav-link-report" @click="handleBackToReportMobile">{{ t('nav.backToReport') }}</button>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { appConfig } from '@/config';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app';
import cptWordmark from '@/assets/images/branding/cpt-wordmark-white.svg';
import LanguageCombobox from '@/components/LanguageCombobox.vue';
import { localePath } from '@/router/localePath';

const { t } = useI18n();
const store = useAppStore();
const router = useRouter();
const route = useRoute();
const menuOpen = ref(false);

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
};

const handleReset = () => {
  menuOpen.value = false;
  if (route.name !== 'home') {
    router.push(localePath('/'));
    window.scrollTo(0, 0);
    return;
  }
  if (store.currentStep === 'support') {
    store.returnFromSupport();
  } else if (store.currentStep === 'report') {
    window.dispatchEvent(new CustomEvent('cpt:report-home'));
    window.scrollTo(0, 0);
  } else {
    store.currentStep = 'landing';
    window.scrollTo(0, 0);
  }
};

// The help system (SupportPage) renders inside MainFlow — the store-driven home
// view — when `store.currentStep === 'support'`. On URL-addressable routes that
// mount a different component (e.g. `/blog`, blog posts, `/data-and-privacy`),
// flipping the store step alone shows nothing, so the link looks dead. Route
// home first (mirrors `handleReset`), then open support, so it works from any
// page in any language.
const openSupport = () => {
  if (route.name !== 'home') {
    router.push(localePath('/'));
  }
  store.navigateToSupport();
};

const handleSupport = () => {
  openSupport();
};

const handleSupportMobile = () => {
  menuOpen.value = false;
  openSupport();
};

const hasActiveSession = computed(() => {
  if (!store.sessionExpiresAt) return false;
  return new Date(store.sessionExpiresAt).getTime() > Date.now();
});

const hasActiveReport = computed(() =>
  hasActiveSession.value &&
  Object.keys(store.vehicleReports).length > 0 &&
  !!store.paymentId
);

const handleBackToReport = () => {
  if (route.name !== 'home') {
    router.push(localePath('/')).then(() => window.scrollTo(0, 0));
  } else {
    window.scrollTo(0, 0);
  }
};

const handleBackToReportMobile = () => {
  menuOpen.value = false;
  handleBackToReport();
};

const handleDeleteData = () => {
  if (window.confirm(t('nav.deleteDataConfirm'))) {
    store.reset();
  }
};

const handleDeleteDataMobile = () => {
  menuOpen.value = false;
  handleDeleteData();
};

const navigateToOAuth = () => {
  if (route.name !== 'home') {
    router.push(localePath('/')).then(() => {
      store.setStep('oauth');
      window.scrollTo(0, 0);
    });
    return;
  }
  store.setStep('oauth');
  window.scrollTo(0, 0);
};

// Locale is initialized from localStorage in i18n/index.ts
// so every component renders in the correct language from the first frame.
</script>

<style scoped>
.nav-link-danger {
  color: var(--color-error, #dc3545) !important;
}
.mobile-nav-link-danger {
  color: var(--color-error, #dc3545) !important;
}
.nav-link-report {
  color: #b8f400 !important;
  font-weight: 600;
}
.mobile-nav-link-report {
  color: #b8f400 !important;
  font-weight: 600;
}
</style>
